import { useMemo, useState } from 'react';
import type { Station } from '@/lib/types';
import { Sheet } from '@/components/ui/sheet';
import { Icon } from '@/components/ui/icon';

interface StationPickerProps {
  label: string;
  stations: Station[];
  selected: Station | null;
  exclude?: Station | null;
  onSelect: (station: Station) => void;
  // Recents scope: station codes are BPP-specific, so recents are namespaced per (city, operator) —
  // otherwise a code that means different stations across two operators (Mumbai) corrupts the list.
  scope?: string;
}

// v3: recents are scoped per (city, operator) since station codes are BPP-specific; the scope is
// appended to the key. (v2 identified stations by BPP catalog `code` instead of the seed numeric id.)
const RECENT_KEY_BASE = 'tickets.recentStations.v3';
const recentKey = (scope: string) => `${RECENT_KEY_BASE}:${scope}`;

// Cap rendered rows so a 150+-stop bus catalog doesn't mount every row at once (the list has no
// virtualization). Recents + the broadened search keep the common cases within the cap.
const RENDER_CAP = 60;

function getRecent(scope: string): string[] {
  try {
    const raw = localStorage.getItem(recentKey(scope));
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function pushRecent(scope: string, code: string) {
  try {
    const next = [code, ...getRecent(scope).filter((x) => x !== code)].slice(0, 5);
    localStorage.setItem(recentKey(scope), JSON.stringify(next));
  } catch {
    // ignore storage failures
  }
}

export function StationPicker({
  label,
  stations,
  selected,
  exclude,
  onSelect,
  scope = '',
}: StationPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Match name, station code, line names, and "interchange" — riders often search by area/line/code,
  // not the exact stop name (`name`-only was too narrow for long bus catalogs).
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    return stations
      .filter((s) => s.code !== exclude?.code)
      .filter((s) => {
        if (!term) return true;
        return (
          s.name.toLowerCase().includes(term) ||
          (s.code ?? '').toLowerCase().includes(term) ||
          s.lines.some((l) => l.name.toLowerCase().includes(term)) ||
          (s.isInterchange && 'interchange'.includes(term))
        );
      });
  }, [stations, query, exclude]);
  const filtered = matches.slice(0, RENDER_CAP);
  const hiddenCount = matches.length - filtered.length;

  const recent = useMemo(() => {
    if (query.trim()) return [];
    const codes = getRecent(scope);
    return codes
      .map((code) => stations.find((s) => s.code === code))
      .filter((s): s is Station => Boolean(s) && s?.code !== exclude?.code);
  }, [stations, query, exclude, scope]);

  function choose(station: Station) {
    if (station.code) pushRecent(scope, station.code);
    onSelect(station);
    setOpen(false);
    setQuery('');
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`wb-field-btn ${selected ? '' : 'is-empty'}`}
      >
        <Icon name="pin" size={20} style={{ color: 'var(--cat-color, var(--honey-700))' }} />
        <span className="wb-col" style={{ alignItems: 'flex-start', gap: 1 }}>
          <span className="wb-tiny">{label}</span>
          <span className="wb-strong" style={{ fontSize: 15.5 }}>
            {selected ? selected.name : 'Select station'}
          </span>
        </span>
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={label}>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <span
            style={{
              position: 'absolute',
              left: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--ink-3)',
            }}
          >
            <Icon name="search" size={19} />
          </span>
          <input
            className="wb-field"
            style={{ paddingLeft: 42 }}
            placeholder="Search stations"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        {recent.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div className="wb-eyebrow" style={{ margin: '0 3px 8px' }}>
              Recent
            </div>
            <div className="wb-col wb-gap-8">
              {recent.map((s) => (
                <StationRow key={`r-${s.code}`} station={s} onClick={() => choose(s)} />
              ))}
            </div>
          </div>
        )}
        <div className="wb-eyebrow" style={{ margin: '0 3px 8px' }}>
          {query.trim() ? `Results (${matches.length})` : `All stations (${matches.length})`}
        </div>
        {filtered.length === 0 ? (
          <p className="wb-sub" style={{ padding: '12px 3px' }}>
            No stations found
          </p>
        ) : (
          <div className="wb-col wb-gap-8">
            {filtered.map((s) => (
              <StationRow key={s.code} station={s} onClick={() => choose(s)} />
            ))}
          </div>
        )}
        {hiddenCount > 0 && (
          <p className="wb-tiny" style={{ padding: '10px 3px', textAlign: 'center' }}>
            Showing {filtered.length} of {matches.length} — keep typing to narrow it down.
          </p>
        )}
      </Sheet>
    </>
  );
}

function StationRow({ station, onClick }: { station: Station; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="wb-option">
      <span className="wb-row wb-gap-6" style={{ flexShrink: 0 }}>
        {station.lines.map((l) => (
          <span
            key={l.id}
            style={{
              width: 9,
              height: 9,
              borderRadius: 999,
              background: l.colorHex || '#999',
              display: 'block',
            }}
            title={l.name}
          />
        ))}
      </span>
      <span className="wb-grow">
        <span className="wb-strong" style={{ fontSize: 15.5, display: 'block' }}>
          {station.name}
        </span>
        <span className="wb-tiny">
          {station.lines.map((l) => l.name).join(' · ')}
          {station.isInterchange && ' · Interchange'}
        </span>
      </span>
      {station.isInterchange && (
        <span className="wb-badge wb-badge--neutral" style={{ fontSize: 11 }}>
          Interchange
        </span>
      )}
    </button>
  );
}
